import React, {useEffect, useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Brand from './Brand';
import LanguageToggle from './LanguageToggle';
import {countryOptions} from '../authCopy';

function FieldError({message}) {
  return message ? <small className="auth-error"><SafeIcon icon={FiIcons.FiAlertCircle} />{message}</small> : null;
}

function AuthPage({mode, language, setLanguage, invitation, onSignIn, onCreateAccount, onBack, t}) {
  const [form, setForm] = useState({
    name: '',
    email: invitation?.email || '',
    country: invitation?.country || '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState('');
  const [welcome, setWelcome] = useState(false);
  const invitationMode = mode === 'invite';

  useEffect(() => {
    setForm((current) => ({
      ...current,
      email: invitation?.email || current.email,
      country: invitation?.country || current.country
    }));
  }, [invitation]);

  const update = (key, value) => setForm((current) => ({...current, [key]: value}));

  const submit = (event) => {
    event.preventDefault();
    const nextErrors = {};

    if (!form.email.trim()) nextErrors.email = t.requiredField;
    if (!invitationMode && !/^\S+@\S+\.\S+$/.test(form.email)) nextErrors.email = t.invalidEmail;
    if (invitationMode && !form.name.trim()) nextErrors.name = t.requiredField;
    if (invitationMode && !form.country) nextErrors.country = t.requiredField;
    if (!form.password) nextErrors.password = t.requiredField;
    else if (invitationMode && form.password.length < 6) nextErrors.password = t.passwordLength;
    if (invitationMode && form.password !== form.confirmPassword) nextErrors.confirmPassword = t.passwordMatch;

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    if (invitationMode) {
      setWelcome(true);
      window.setTimeout(() => onCreateAccount({...form, language}), 1200);
    } else {
      onSignIn({...form, language});
    }
  };

  if (invitationMode && invitation?.status === 'expired') {
    return (
      <main className="auth-screen">
        <div className="auth-topbar">
          <Brand auth />
          <LanguageToggle language={language} onChange={setLanguage} label={t.language} english={t.english} spanish={t.spanish} />
        </div>
        <section className="auth-card unavailable-card">
          <SafeIcon icon={FiIcons.FiAlertCircle} />
          <h1>{t.invitationUnavailable}</h1>
          <button type="button" className="auth-link-button" onClick={onBack}>{t.signIn}</button>
        </section>
      </main>
    );
  }

  if (welcome) {
    return (
      <main className="auth-screen">
        <div className="auth-topbar">
          <Brand auth />
          <LanguageToggle language={language} onChange={setLanguage} label={t.language} english={t.english} spanish={t.spanish} />
        </div>
        <motion.section className="auth-card welcome-auth-card" initial={{opacity: 0, scale: 0.96}} animate={{opacity: 1, scale: 1}}>
          <span className="welcome-check"><SafeIcon icon={FiIcons.FiCheck} /></span>
          <h1>{t.welcomeTitle}</h1>
          <p>{t.welcomeHelper}</p>
        </motion.section>
      </main>
    );
  }

  return (
    <main className="auth-screen">
      <div className="auth-topbar">
        <Brand auth />
        <LanguageToggle language={language} onChange={setLanguage} label={t.language} english={t.english} spanish={t.spanish} />
      </div>

      <motion.section className="auth-card" initial={{opacity: 0, y: 16}} animate={{opacity: 1, y: 0}}>
        {invitationMode ? (
          <>
            <span className="eyebrow">{t.invitationEyebrow}</span>
            <h1>{t.invitedBy} <em>{invitation?.invitedBy || 'GFI International'}</em> {t.invitedTo}</h1>
            <p>{t.accountHelper}</p>
          </>
        ) : (
          <>
            <span className="eyebrow">{t.invitationEyebrow}</span>
            <h1>{t.signInTitle}</h1>
            <p>{t.signInHelper}</p>
          </>
        )}

        <form className="auth-form" onSubmit={submit}>
          {invitationMode && (
            <label>
              {t.fullName}
              <input value={form.name} onChange={(event) => update('name', event.target.value)} />
              <FieldError message={errors.name} />
            </label>
          )}

          <label>
            {t.email}
            <input type="email" value={form.email} readOnly={invitationMode} onChange={(event) => update('email', event.target.value)} />
            <FieldError message={errors.email} />
          </label>

          {invitationMode && (
            <div className="auth-two-fields">
              <label>
                {t.preferredLanguage}
                <select value={language} onChange={(event) => setLanguage(event.target.value)}>
                  <option value="en">English</option>
                  <option value="es">Español</option>
                </select>
              </label>

              <label>
                {t.country}
                <select value={form.country} onChange={(event) => update('country', event.target.value)}>
                  <option value="">—</option>
                  {countryOptions.map((country) => <option key={country} value={country}>{t.countries[country]}</option>)}
                </select>
                <FieldError message={errors.country} />
              </label>
            </div>
          )}

          <label>
            {invitationMode ? t.createPassword : t.password}
            <input type="password" value={form.password} onChange={(event) => update('password', event.target.value)} />
            <FieldError message={errors.password} />
          </label>

          {invitationMode && (
            <label>
              {t.confirmPassword}
              <input type="password" value={form.confirmPassword} onChange={(event) => update('confirmPassword', event.target.value)} />
              <FieldError message={errors.confirmPassword} />
            </label>
          )}

          {!invitationMode && (
            <button
              type="button"
              className="auth-link-button forgot-link"
              onClick={() => {
                setToast(t.forgotPasswordToast);
                window.setTimeout(() => setToast(''), 3200);
              }}
            >
              {t.forgotPassword}
            </button>
          )}

          <button type="submit" className="primary-button auth-submit">
            <SafeIcon icon={invitationMode ? FiIcons.FiUserCheck : FiIcons.FiLogIn} />
            {invitationMode ? t.createAccount : t.signIn}
          </button>
        </form>

        {!invitationMode && (
          <p className="auth-contact-note">
            {t.noInvitation} <button type="button" onClick={() => setToast(t.contactRecruiter)}>{t.contactRecruiter}</button>
          </p>
        )}

        <button type="button" className="auth-back-button" onClick={onBack}>
          {invitationMode ? t.signIn : t.invitationLink}
        </button>
      </motion.section>

      {toast && (
        <div className="auth-toast" role="status">
          <SafeIcon icon={FiIcons.FiInfo} />
          {toast}
        </div>
      )}
    </main>
  );
}

export default AuthPage;